import { type JSX, useCallback, useEffect, useRef, useState } from 'react';

import {
  entwurfsvorschau,
  neueFassung,
  platzhalter as holePlatzhalter,
  vorlage as holeVorlage,
  vorlagen as holeVorlagen,
  type Belegart,
  type Platzhalter,
  type Vorlage,
  type Vorlagenzeile,
} from '../api/belegausgabe';
import { ApiFehler } from '../api/fehler';
import { Reiterleiste } from '../komponenten/Akte';
import { Einstellungsseite, Thema } from '../komponenten/Einstellungen';
import { Meldung } from '../komponenten/Meldung';
import { KUNDENARTNAME, type Kundenart } from '../api/kunden';

const BELEGARTNAME: Readonly<Record<Belegart, string>> = {
  angebot: 'Angebot',
  auftrag: 'Auftrag',
  rechnung: 'Rechnung',
};

/**
 * Die Vorlagenpflege (ANG-12, ANG-13).
 *
 * „PDF-Ausgabe über **gestaltbare** Vorlagen mit Briefkopf, Logo, Schriftbild,
 * Seitenzahlen und Platzhaltern für alle Beleg- und Kundenfelder."
 *
 * DREI DINGE STEHEN NEBENEINANDER, und die Anordnung ist die Aussage:
 *
 *   links   die Platzhalter, die es gibt — anklickbar, weil niemand eine
 *           Liste von dreißig Schlüsseln abtippt, ohne sich zu vertippen
 *   mitte   der Text
 *   rechts  die Vorschau mit Beispieldaten
 *
 * SPEICHERN ERZEUGT EINE NEUE FASSUNG (AUF-08) und überschreibt nichts. Die
 * Vorschau nimmt deshalb den ENTWURF: Sähe man jede Änderung erst nach dem
 * Speichern, hätte man nach einem Nachmittag Gestaltung dreißig Fassungen.
 *
 * SEITENTYP EINSTELLUNGEN (ÄA-41): Die Übersicht ist ein Thema mit der
 * Tabelle der Vorlagen. Im Editor bleiben Text und Platzhalter nebeneinander
 * (zwei Drittel zu einem) — die Anordnung ist die Aussage und wird nicht in
 * das Raster der Themen gezwängt; Vorschau und Fassungen folgen als eigene
 * Karten.
 */
export function Belegvorlagen({
  darfPflegen,
}: {
  readonly darfPflegen: boolean;
}): JSX.Element {
  const [liste, setzeListe] = useState<readonly Vorlagenzeile[]>([]);
  const [offen, setzeOffen] = useState<Vorlage | undefined>(undefined);
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);
  const [rueckmeldung, setzeRueckmeldung] = useState('');

  const laden = useCallback(async (): Promise<void> => {
    try {
      setzeListe(await holeVorlagen());
      setzeFehler(undefined);
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    }
  }, []);

  useEffect(() => {
    void laden();
  }, [laden]);

  async function oeffne(id: string): Promise<void> {
    try {
      setzeOffen(await holeVorlage(id));
      setzeRueckmeldung('');
      setzeFehler(undefined);
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    }
  }

  if (offen !== undefined) {
    return (
      <Bearbeiten
        vorlage={offen}
        darfPflegen={darfPflegen}
        beiZurueck={() => {
          setzeOffen(undefined);
          void laden();
        }}
        beiGespeichert={(neu, version) => {
          setzeRueckmeldung(`Fassung ${String(version)} gespeichert.`);
          void oeffne(neu);
        }}
      />
    );
  }

  return (
    <div className="stapel">
      {fehler !== undefined && <Meldung fehler={fehler} />}
      {rueckmeldung !== '' && (
        <p className="hinweiszeile" role="status">
          {rueckmeldung}
        </p>
      )}

      <p className="hilfe">
        Je Belegart und Kundenart gilt genau eine aktive Vorlage; gibt es keine besondere, greift
        die allgemeine. Eine Änderung erzeugt eine neue Fassung — ältere bleiben lesbar,
        weil ein bereits erzeugter Beleg auf sie zeigt.
      </p>

      <Einstellungsseite>
        <Thema titel="Vorlagen" zusatz={`${String(liste.length)} Vorlagen`} breit>
          <div className="tabellenhuelle tabellenhuelle--telefon">
            <table className="liste__tabelle">
              <caption className="nur-vorlesen">{liste.length} Vorlagen</caption>
              <thead>
                <tr>
                  <th scope="col">Kennung</th>
                  <th scope="col">Bezeichnung</th>
                  <th scope="col">Belegart</th>
                  <th scope="col">gilt für</th>
                  <th scope="col">Fassung</th>
                  <th scope="col">Status</th>
                </tr>
              </thead>
              <tbody>
                {liste.map((v) => (
                  <tr key={v.id}>
                    <td>
                      <button type="button" className="liste__oeffnen liste__nummer" onClick={() => void oeffne(v.id)}>
                        {v.kennung}
                      </button>
                    </td>
                    <td>{v.bezeichnung}</td>
                    <td>{BELEGARTNAME[v.belegart]}</td>
                    <td>
                      {v.kundenart === null ? (
                        <span className="feld__hilfe">alle Kundenarten</span>
                      ) : (
                        (KUNDENARTNAME[v.kundenart as Kundenart] ?? v.kundenart)
                      )}
                    </td>
                    <td>{v.version}</td>
                    <td>
                      {v.aktiv ? (
                        <span className="marke">aktiv</span>
                      ) : (
                        <span className="marke marke--inaktiv">Entwurf</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {liste.length === 0 && (
            <p className="feld__hilfe">Noch keine Vorlage. Zwei werden mit dem System ausgeliefert.</p>
          )}
        </Thema>
      </Einstellungsseite>
    </div>
  );
}

/**
 * Der Editor.
 *
 * DIE VORSCHAU LÄUFT VERZÖGERT UND NICHT BEI JEDEM TASTENDRUCK. 400
 * Millisekunden Ruhe genügen, damit ein Wort zu Ende getippt ist; ohne die
 * Verzögerung liefe je Zeichen eine Anfrage, und die Vorschau flackerte.
 */
function Bearbeiten({
  vorlage,
  darfPflegen,
  beiZurueck,
  beiGespeichert,
}: {
  readonly vorlage: Vorlage;
  readonly darfPflegen: boolean;
  readonly beiZurueck: () => void;
  readonly beiGespeichert: (id: string, version: number) => void;
}): JSX.Element {
  const [bezeichnung, setzeBezeichnung] = useState(vorlage.bezeichnung);
  const [rumpf, setzeRumpf] = useState(vorlage.rumpfHtml);
  const [kopf, setzeKopf] = useState(vorlage.kopfHtml ?? '');
  const [fuss, setzeFuss] = useState(vorlage.fussHtml ?? '');
  const [stil, setzeStil] = useState(vorlage.stilCss ?? '');
  const [teil, setzeTeil] = useState<'rumpf' | 'kopf' | 'fuss' | 'stil'>('rumpf');
  const [katalog, setzeKatalog] = useState<readonly Platzhalter[]>([]);
  const [vorschau, setzeVorschau] = useState('');
  const [unbekannt, setzeUnbekannt] = useState<readonly string[]>([]);
  const [fehlendeAbschnitte, setzeFehlendeAbschnitte] = useState<readonly string[]>([]);
  const [laeuft, setzeLaeuft] = useState(false);
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);
  const feld = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    void holePlatzhalter(vorlage.belegart)
      .then(setzeKatalog)
      .catch(() => setzeKatalog([]));
  }, [vorlage.belegart]);

  useEffect(() => {
    const uhr = setTimeout(() => {
      void entwurfsvorschau({
        belegart: vorlage.belegart,
        rumpfHtml: rumpf,
        kopfHtml: kopf === '' ? null : kopf,
        fussHtml: fuss === '' ? null : fuss,
        stilCss: stil === '' ? null : stil,
      })
        .then((e) => {
          setzeVorschau(e.html);
          setzeUnbekannt(e.unbekannt);
          setzeFehlendeAbschnitte(e.fehlendeAbschnitte);
        })
        .catch(() => setzeVorschau(''));
    }, 400);
    return () => clearTimeout(uhr);
  }, [vorlage.belegart, rumpf, kopf, fuss, stil]);

  /**
   * Einen Platzhalter an der Schreibmarke einsetzen.
   *
   * AN DER SCHREIBMARKE UND NICHT AM ENDE: Wer mitten in einer Tabellenzeile
   * steht, will ihn dort haben. Ein Anhängen am Ende wäre eine Handlung, die
   * man danach rückgängig macht.
   */
  function setzeEin(schluessel: string, istListe: boolean): void {
    const text = istListe
      ? `{{#${schluessel}}}\n  {{zeile.bezeichnung}}\n{{/${schluessel}}}`
      : `{{${schluessel}}}`;
    const bereich = feld.current;
    const setzer = { rumpf: setzeRumpf, kopf: setzeKopf, fuss: setzeFuss, stil: setzeStil }[teil];
    const wert = { rumpf, kopf, fuss, stil }[teil];

    if (bereich === null) {
      setzer(wert + text);
      return;
    }
    const von = bereich.selectionStart;
    const bis = bereich.selectionEnd;
    setzer(wert.slice(0, von) + text + wert.slice(bis));
    // Erst nach dem Zeichnen — vorher steht der neue Wert noch nicht im Feld.
    window.setTimeout(() => {
      bereich.focus();
      bereich.setSelectionRange(von + text.length, von + text.length);
    }, 0);
  }

  async function speichern(): Promise<void> {
    setzeLaeuft(true);
    try {
      const neu = await neueFassung(vorlage.id, {
        bezeichnung,
        rumpfHtml: rumpf,
        kopfHtml: kopf === '' ? null : kopf,
        fussHtml: fuss === '' ? null : fuss,
        stilCss: stil === '' ? null : stil,
      });
      setzeFehler(undefined);
      beiGespeichert(neu.id, neu.version);
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    } finally {
      setzeLaeuft(false);
    }
  }

  const inhalt = { rumpf, kopf, fuss, stil }[teil];
  const setzeInhalt = { rumpf: setzeRumpf, kopf: setzeKopf, fuss: setzeFuss, stil: setzeStil }[teil];

  return (
    <div className="stapel">
      {fehler !== undefined && <Meldung fehler={fehler} />}

      <div className="knopfreihe">
        <button type="button" className="knopf knopf--klein" onClick={beiZurueck}>
          Zur Liste
        </button>
      </div>

      {vorlage.abgeloest && (
        <p className="warnung">
          Diese Fassung ist abgelöst und bleibt unverändert einsehbar. Ein bereits
          erzeugter Beleg zeigt auf sie. Öffnen Sie die geltende Fassung, um zu ändern.
        </p>
      )}

      {unbekannt.length > 0 && (
        <p className="warnung">
          Unbekannte Platzhalter: {unbekannt.join(', ')}. Sie bleiben im Kundendokument stehen —
          gespeichert wird die Vorlage so nicht.
        </p>
      )}

      {/* PRF-17 (CRM-169): sonst steht ein angerechneter Gutschein nicht auf dem Beleg. */}
      {fehlendeAbschnitte.length > 0 && (
        <p className="warnung">
          Es fehlen die Abschnitte {fehlendeAbschnitte.map((a) => `{{#${a}}}`).join(', ')}.{' '}
          {vorlage.belegart === 'rechnung'
            ? 'Ohne sie steht ein angerechneter Gutschein nicht auf der Rechnung, obwohl der '
              + 'Zahlbetrag ihn abzieht. Setzen Sie sie unter die Zeile mit dem Rechnungsbetrag.'
            : /* AeA-69 A */ 'Ohne sie nennt der Beleg weder die Gutscheine noch den Betrag, der zu '
              + 'zahlen ist. Setzen Sie sie unter die Zeile mit dem Gesamtbetrag.'}
        </p>
      )}

      <div className="spalten spalten--zweidrittel">
        <Thema
          titel={`${vorlage.kennung} · Fassung ${String(vorlage.version)}`}
          zusatz={BELEGARTNAME[vorlage.belegart]}
        >

          <div className="feld">
            <label className="feld__beschriftung" htmlFor="vl-bezeichnung">
              Bezeichnung
            </label>
            <input
              id="vl-bezeichnung"
              type="text"
              value={bezeichnung}
              disabled={!darfPflegen || vorlage.abgeloest}
              onChange={(e) => setzeBezeichnung(e.target.value)}
            />
          </div>

          <Reiterleiste
            beschriftung="Teil der Vorlage"
            praefix="vl-"
            reiter={[
              { kennung: 'rumpf', beschriftung: 'Rumpf' },
              { kennung: 'kopf', beschriftung: 'Briefkopf (je Seite)' },
              { kennung: 'fuss', beschriftung: 'Fußzeile (je Seite)' },
              { kennung: 'stil', beschriftung: 'Schriftbild (CSS)' },
            ]}
            gewaehlt={teil}
            beiWahl={setzeTeil}
          />

          <div
            className="feld"
            role="tabpanel"
            id={`tafel-vl-${teil}`}
            aria-labelledby={`reiter-vl-${teil}`}
          >
            <label className="feld__beschriftung" htmlFor="vl-inhalt">
              {teil === 'stil' ? 'CSS' : 'HTML'}
            </label>
            <textarea
              id="vl-inhalt"
              ref={feld}
              value={inhalt}
              rows={22}
              spellCheck={false}
              disabled={!darfPflegen || vorlage.abgeloest}
              className="feld__eingabe feld__eingabe--quelltext"
              onChange={(e) => setzeInhalt(e.target.value)}
            />
            {teil === 'kopf' && (
              <p className="feld__hilfe">
                Wird auf <strong>jeder Seite</strong> gesetzt. Hier gehören Briefkopf und Logo hin
                — und nur hier kennt der Renderer die Seitenzahl.
              </p>
            )}
            {teil === 'fuss' && (
              <p className="feld__hilfe">
                <code>{'<span class="pageNumber"></span>'}</code> und{' '}
                <code>{'<span class="totalPages"></span>'}</code> setzt der Renderer ein. Ein Logo
                gehört als <code>data:</code>-URI in die Vorlage — der Renderer lädt nichts nach.
              </p>
            )}
          </div>

          {darfPflegen && !vorlage.abgeloest && (
            <div className="knopfreihe">
              <button
                type="button"
                className="knopf knopf--haupt"
                disabled={laeuft || unbekannt.length > 0}
                onClick={() => void speichern()}
              >
                {laeuft ? 'Wird gespeichert …' : 'Als neue Fassung speichern'}
              </button>
              <span className="feld__hilfe">
                Fassung {vorlage.version} bleibt danach unverändert lesbar.
              </span>
            </div>
          )}
        </Thema>

        <aside className="karte" aria-label="Verfügbare Platzhalter">
          <div className="karte__titel">
            <h2>Platzhalter</h2>
            {/* ANG-12 */}
          </div>
          <p className="feld__hilfe">
            Anklicken setzt den Platzhalter an der Schreibmarke ein. Ein Block wiederholt sich je
            Zeile; darin stehen die Felder als <code>{'{{zeile.feld}}'}</code>.
          </p>
          <ul className="kanalliste">
            {katalog.map((p) => (
              <li key={p.schluessel}>
                <button
                  type="button"
                  className="knopf knopf--klein"
                  disabled={!darfPflegen || vorlage.abgeloest}
                  onClick={() => setzeEin(p.schluessel, p.istListe)}
                >
                  {p.istListe ? `#${p.schluessel}` : p.schluessel}
                </button>
                <span className="kanalliste__name">
                  {' '}
                  {p.bezeichnung}
                  {!p.istListe && ` · z. B. „${p.beispiel}“`}
                </span>
              </li>
            ))}
          </ul>
        </aside>
      </div>

      <Thema titel="Vorschau" zusatz="mit Beispieldaten">
        <p className="feld__hilfe">
          Dasselbe Markup, das zu PDF wird — mit erfundenen Werten, damit sich eine Vorlage auch
          im leeren System einrichten lässt. Kopf und Fuß stehen hier einmal oben und einmal
          unten; im PDF wiederholen sie sich je Seite.
        </p>
        <iframe
          title="Vorlagenvorschau"
          srcDoc={vorschau}
          sandbox=""
          className="belegvorschau"
        />
      </Thema>

      {/* AUF-08 */}
      {vorlage.fassungen.length > 1 && (
        <Thema titel="Fassungen">
          <ul className="kanalliste">
            {vorlage.fassungen.map((f) => (
              <li key={f.id}>
                Fassung {f.version}
                {f.abgeloest && <span className="marke marke--inaktiv">abgelöst</span>}
                {f.id === vorlage.id && <span className="marke">hier</span>}
                <span className="kanalliste__name"> · {f.angelegtAm.slice(0, 10)}</span>
              </li>
            ))}
          </ul>
        </Thema>
      )}
    </div>
  );
}
